import { MicrophoneCheckStatus } from './microphone-check.model';

/**
 * Whether this browser can give a call a microphone, known before any call is placed.
 *
 * - `unknown`: not checked yet, or the browser exposes nothing to check with. Never blocks a call.
 * - `available`: at least one input device exists and access is not refused. Access may still be
 *   asked for (`prompt`) — that is the browser's own dialog, not a reason to stop.
 * - `permission-denied`: access to the microphone is blocked for this site.
 * - `no-device`: the browser lists no audio input at all.
 */
export type MicrophoneAvailability = 'unknown' | 'available' | 'permission-denied' | 'no-device';

export const isMicrophoneUnavailable = (availability: MicrophoneAvailability): availability is 'permission-denied' | 'no-device' =>
  availability === 'permission-denied' || availability === 'no-device';

/** The microphone problems the required-microphone dialog explains, each with its own fix. */
export type MicrophoneProblem = Extract<
  MicrophoneCheckStatus,
  'permission-denied' | 'no-device' | 'track-ended' | 'track-muted' | 'silent' | 'loopback-device'
>;

export interface MicrophoneRequiredDialogData {
  problem: MicrophoneProblem;
}

export interface MicrophoneRequiredDialogResult {
  /** True when the dialog closed because the microphone became available while it was open. */
  resolved: boolean;
}

export interface MicrophoneProblemHelp {
  title: string;
  /** What is wrong, in one sentence. */
  summary: string;
  /** What to do, in order. Browser-agnostic where possible: the team uses Chrome and Edge. */
  steps: string[];
}

const CHECK_HEADSET_STEPS = [
  'Make sure your headset is connected and its mute switch is off.',
  'In your computer’s sound settings, check that the headset is selected as the input device and that its volume is not at zero.',
  'Click “Try again”.',
];

export const MICROPHONE_PROBLEM_HELP: Record<MicrophoneProblem, MicrophoneProblemHelp> = {
  'permission-denied': {
    title: 'Microphone access is blocked',
    summary: 'This site is not allowed to use your microphone, so you cannot place or answer calls.',
    steps: [
      'Click the icon to the left of the web address (a lock or settings icon).',
      'Find “Microphone” and change it to “Allow”.',
      'Click “Try again”. If calls still do not work, reload the page.',
    ],
  },
  'no-device': {
    title: 'No microphone detected',
    summary: 'Your computer is not reporting any microphone, so you cannot place or answer calls.',
    steps: CHECK_HEADSET_STEPS,
  },
  'track-ended': {
    title: 'Your microphone stopped responding',
    summary: 'The microphone was disconnected or stopped working since it was last used.',
    steps: ['Unplug your headset and plug it back in.', ...CHECK_HEADSET_STEPS.slice(1)],
  },
  'track-muted': {
    title: 'Your microphone is muted',
    summary: 'Your computer is keeping the microphone muted, so the patient would hear silence.',
    steps: CHECK_HEADSET_STEPS,
  },
  silent: {
    title: 'Your microphone is not picking up sound',
    summary: 'The microphone is connected but captured nothing, so the patient would hear silence.',
    steps: CHECK_HEADSET_STEPS,
  },
  'loopback-device': {
    title: 'Your computer is not using a microphone',
    summary: 'The selected input is "Stereo Mix" or a virtual cable: it records what your computer plays, not your voice.',
    steps: [
      'Open your computer’s sound settings (Windows: Settings → System → Sound → Input).',
      'Select your headset or microphone as the input device.',
      'If you do not use “Stereo Mix”, disable it so it is not picked again.',
      'Click “Try again”, then call again. If the call still uses the wrong input, reload the page.',
    ],
  },
};

export const isMicrophoneProblem = (status: string): status is MicrophoneProblem => status in MICROPHONE_PROBLEM_HELP;
